import { Gavel, Video, FileText, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import lawBuildingImage from '@/assets/law-building.jpg';
import { Link } from 'react-router-dom';
import HeroContact from '@/components/HeroContact';
import { useEffect, useState } from 'react';

// Animation component for text appearing line by line
const AnimatedParagraph = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <p
      className={`text-xl text-muted-foreground mb-12 leading-relaxed max-w-3xl mx-auto transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      {children}
    </p>
  );
};

// Animation component for sections appearing
const AnimatedSection = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className={`transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      {children}
    </div>
  );
};

const WorkWithUs = () => {
  return (
    <section id="work-with-us" className="pt-32 pb-20 bg-background">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <h2 className="font-rockwell text-3xl md:text-5xl font-semibold text-primary mb-6">
          Work with Freeman & Crown<br className="hidden md:block" /> Equity Law Group
        </h2>
        <AnimatedParagraph delay={200}>
          We provide all of our processes and tools completely free. With that said, we do require large donations for the time and energy directed at your specific situation. Choose how we may serve you below.
        </AnimatedParagraph>
      </div>

      <div className="max-w-2xl md:max-w-6xl mx-auto px-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Discharge Mortgage/Car Note Button - First */}
          <AnimatedSection delay={400}>
            <Link to="/services/discharge-loan" className="block">
              <div className="group bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-8 border border-gold/20 hover:border-gold/40 transition-all duration-500 ease-out hover:shadow-lg hover:shadow-gold/20 h-[180px] md:h-[280px]">
                <div className="h-full flex flex-col justify-between">
                  <div>
                    <div className="mb-4">
                      <div className="w-12 h-1 bg-primary mb-6"></div>
                    </div>
                    <h3 className="font-rockwell text-2xl font-semibold text-primary mb-4 leading-tight">
                      Discharge Mortgages, Car Loans & Credit Cards
                    </h3>
                    <p className="text-muted-foreground mb-6 leading-relaxed text-sm hidden md:block">
                      Legal assistance to discharge your mortgage, car loan, or credit card debt through proper lawful procedure.
                    </p>
                  </div>
                  <div className="mt-auto flex justify-start">
                    <Button 
                      variant="link"
                      className="text-primary hover:text-gold p-0 h-auto text-sm font-medium flex items-center gap-1 pointer-events-none"
                    >
                      Discharge My Debt <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </Link>
          </AnimatedSection>

          {/* Trust Formation Button - Second */}
          <AnimatedSection delay={600}>
            <Link to="/services/private-trust" className="block">
              <div className="group bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-8 border border-gold/20 hover:border-gold/40 transition-all duration-500 ease-out hover:shadow-lg hover:shadow-gold/20 h-[180px] md:h-[280px]">
                <div className="h-full flex flex-col justify-between">
                  <div>
                    <div className="mb-4">
                      <div className="w-12 h-1 bg-primary mb-6"></div>
                    </div>
                    <h3 className="font-rockwell text-2xl font-semibold text-primary mb-4 leading-tight">
                      Trust Formation / Estate Planning
                    </h3>
                    <p className="text-muted-foreground mb-6 leading-relaxed text-sm hidden md:block">
                      Professional trust establishment with full documentation within 10 days to protect your assets and privacy.
                    </p>
                  </div>
                  <div className="mt-auto flex justify-start">
                    <Button 
                      variant="link"
                      className="text-primary hover:text-gold p-0 h-auto text-sm font-medium flex items-center gap-1 pointer-events-none"
                    >
                      Establish My Trust <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </Link>
          </AnimatedSection>

          {/* Zero Your IRS Tax Obligation Button - Third */}
          <AnimatedSection delay={800}>
            <Link to="/services/tax-liability" className="block">
              <div className="group bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-8 border border-gold/20 hover:border-gold/40 transition-all duration-500 ease-out hover:shadow-lg hover:shadow-gold/20 h-[180px] md:h-[280px]">
                <div className="h-full flex flex-col justify-between">
                  <div>
                    <div className="mb-4">
                      <div className="w-12 h-1 bg-primary mb-6"></div>
                    </div>
                    <h3 className="font-rockwell text-2xl font-semibold text-primary mb-4 leading-tight">
                      Zero your IRS Tax Obligation
                    </h3>
                    <p className="text-muted-foreground mb-6 leading-relaxed text-sm hidden md:block">
                      Assistance to eliminate IRS tax obligations through proper lawful procedure.
                    </p>
                  </div>
                  <div className="mt-auto flex justify-start">
                    <Button 
                      variant="link"
                      className="text-primary hover:text-gold p-0 h-auto text-sm font-medium flex items-center gap-1 pointer-events-none"
                    >
                      Zero My Tax Bill <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </Link>
          </AnimatedSection>
        </div>

        {/* Second row with Tax ID, Litigation and Consulting */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
          {/* Obtain U.S. Tax ID Number Button - Fourth */}
          <AnimatedSection delay={1000}>
            <Link to="/services/tax-id" className="block">
              <div className="group bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-8 border border-gold/20 hover:border-gold/40 transition-all duration-500 ease-out hover:shadow-lg hover:shadow-gold/20 h-[180px] md:h-[280px]">
                <div className="h-full flex flex-col justify-between">
                  <div>
                    <div className="mb-4">
                      <div className="w-12 h-1 bg-primary mb-6"></div>
                    </div>
                    <h3 className="font-rockwell text-2xl font-semibold text-primary mb-4 leading-tight">
                      Obtain a U.S. Tax ID Number
                    </h3>
                    <p className="text-muted-foreground mb-6 leading-relaxed text-sm hidden md:block">
                      Assistance to obtain your U.S. Tax ID Number (ITIN) for people foreign to the U.S.
                    </p>
                  </div>
                  <div className="mt-auto flex justify-start">
                    <Button 
                      variant="link"
                      className="text-primary hover:text-gold p-0 h-auto text-sm font-medium flex items-center gap-1 pointer-events-none"
                    >
                      Get My Tax ID <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </Link>
          </AnimatedSection>

          {/* Litigation Button - Fifth */}
          <AnimatedSection delay={1200}>
            <Link to="/litigation" className="block">
              <div className="group bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-8 border border-gold/20 hover:border-gold/40 transition-all duration-500 ease-out hover:shadow-lg hover:shadow-gold/20 h-[180px] md:h-[280px]">
                <div className="h-full flex flex-col justify-between">
                  <div>
                    <div className="mb-4">
                      <div className="w-12 h-1 bg-primary mb-6"></div>
                    </div>
                    <h3 className="font-rockwell text-2xl font-semibold text-primary mb-4 leading-tight">
                      Litigation
                    </h3>
                    <p className="text-muted-foreground mb-6 leading-relaxed text-sm hidden md:block">
                      In-depth lawful guidance and litigation support for complex cases requiring court action and constitutional defense or offense.
                    </p>
                  </div>
                  <div className="mt-auto flex justify-start">
                    <Button 
                      variant="link"
                      className="text-primary hover:text-gold p-0 h-auto text-sm font-medium flex items-center gap-1 pointer-events-none"
                    >
                      Learn More <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </Link>
          </AnimatedSection>

          {/* Phone Consultation Button - Sixth (Last) */}
          <AnimatedSection delay={1400}>
            <Link to="/phone-consultation" className="block">
              <div className="group bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-8 border border-gold/20 hover:border-gold/40 transition-all duration-500 ease-out hover:shadow-lg hover:shadow-gold/20 h-[180px] md:h-[280px]">
                <div className="h-full flex flex-col justify-between">
                  <div>
                    <div className="mb-4">
                      <div className="w-12 h-1 bg-primary mb-6"></div>
                    </div>
                    <h3 className="font-rockwell text-2xl font-semibold text-primary mb-4 leading-tight">
                      Consulting
                    </h3>
                    <p className="text-muted-foreground mb-6 leading-relaxed text-sm hidden md:block">
                      One-on-one guidance and consultation to discuss your situation and determine the best path forward.
                    </p>
                  </div>
                  <div className="mt-auto flex justify-start">
                    <Button 
                      variant="link"
                      className="text-primary hover:text-gold p-0 h-auto text-sm font-medium flex items-center gap-1 pointer-events-none"
                    >
                      Book Now <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </Link>
          </AnimatedSection>
        </div>
      </div>
      
      <div id="contact">
        <HeroContact noBackground={true} />
      </div>
      
      <div className="max-w-2xl md:max-w-6xl mx-auto px-4 mt-12">
        <AnimatedSection delay={1600}>
          <Link to="/education" className="block">
            <div className="p-8 bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl border border-gold/20 hover:border-gold/40 transition-all duration-500 ease-out hover:shadow-lg hover:shadow-gold/20">
              <div className="mb-4">
                <div className="w-12 h-1 bg-primary mb-6"></div>
              </div>
              <h3 className="font-rockwell text-2xl font-semibold text-primary mb-4 leading-tight">
                Educate Yourself and Act
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-6 text-sm">
                We are committed to protecting the constitutional rights of all men and women on the land---and because of this, we make all of our resources and processes completely free so that you are empowered to act on your own without needing anyone or anything or even money for that matter. We give it all for free and if you want to do it that way just click the button below.
              </p>
              <div className="flex justify-start">
                <Button 
                  variant="link"
                  className="text-primary hover:text-gold p-0 h-auto text-sm font-medium flex items-center gap-1 pointer-events-none"
                >
                  Learn More <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Link>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default WorkWithUs;